import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Send, Paperclip, Loader2, X, FileText, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

export default function ComposeEmail({ 
  open, 
  onOpenChange, 
  projectId, 
  projectName,
  availableFiles = [],
  onEmailSent,
  userMailbox
}) {
  const [toEmails, setToEmails] = useState("");
  const [ccEmails, setCcEmails] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [showCc, setShowCc] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setToEmails("");
      setCcEmails("");
      setSubject("");
      setBody("");
      setSelectedFiles([]);
      setShowCc(false);
      setShowAttachments(false);
    }
  }, [open]);

  const handleFileToggle = (fileId) => {
    setSelectedFiles(prev => 
      prev.includes(fileId) 
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const handleSend = async () => {
    if (!toEmails.trim()) {
      toast.error("Please enter at least one recipient");
      return;
    }

    if (!subject.trim()) {
      toast.error("Please enter a subject");
      return;
    }

    setSending(true);

    try {
      const toList = toEmails.split(",").map(e => e.trim()).filter(e => e);
      const ccList = ccEmails ? ccEmails.split(",").map(e => e.trim()).filter(e => e) : [];

      const response = await axios.post(
        `${API}/email/compose`,
        {
          project_id: projectId,
          to_emails: toList,
          cc_emails: ccList,
          subject: subject,
          body: body,
          attachment_file_ids: selectedFiles
        },
        getAuthHeaders()
      );

      if (response.data.success) {
        toast.success("Email sent successfully!");
        onOpenChange(false);
        if (onEmailSent) {
          onEmailSent(response.data);
        }
      } else {
        toast.error(response.data.message || "Failed to send email");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const approvedFiles = availableFiles.filter(f => f.status === "Approved");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-600" />
            Compose Email
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Project Context */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>Project:</strong> {projectName} ({projectId})
            </p>
            {userMailbox?.mailbox?.email && (
              <p className="text-sm text-blue-800 mt-1">
                <strong>From:</strong> {userMailbox.mailbox.email}
              </p>
            )}
            <p className="text-xs text-blue-600 mt-1">
              Project ID will be added to subject • UUID will be embedded for reply tracking
            </p>
          </div>

          {/* To Field */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label htmlFor="to">To</Label>
              {!showCc && (
                <button 
                  type="button"
                  onClick={() => setShowCc(true)}
                  className="text-xs text-blue-600 hover:underline"
                >
                  + Add Cc
                </button>
              )}
            </div>
            <Input
              id="to"
              value={toEmails}
              onChange={(e) => setToEmails(e.target.value)}
              placeholder="recipient@example.com (comma-separated for multiple)"
              data-testid="compose-to-input"
            />
          </div>

          {/* CC Field */}
          {showCc && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="cc">Cc</Label>
                <button 
                  type="button"
                  onClick={() => {
                    setShowCc(false);
                    setCcEmails("");
                  }}
                  className="text-xs text-gray-500 hover:text-red-500"
                >
                  Remove
                </button>
              </div>
              <Input
                id="cc"
                value={ccEmails}
                onChange={(e) => setCcEmails(e.target.value)}
                placeholder="cc@example.com (comma-separated for multiple)"
                data-testid="compose-cc-input"
              />
            </div>
          )}

          {/* Subject Field */}
          <div>
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject (project ID will be auto-added)"
              data-testid="compose-subject-input"
            />
          </div>

          {/* Body Field */}
          <div>
            <Label htmlFor="body">Message</Label>
            <Textarea
              id="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Type your message here..."
              rows={8}
              data-testid="compose-body-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Project signature with tracking UUID will be automatically added
            </p>
          </div>

          {/* Attachments Section */}
          {approvedFiles.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowAttachments(!showAttachments)}
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
              >
                <Paperclip className="w-4 h-4" />
                {showAttachments ? "Hide" : "Attach"} Project Documents
                {selectedFiles.length > 0 && (
                  <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs">
                    {selectedFiles.length} selected
                  </span>
                )}
              </button>

              {showAttachments && (
                <div className="mt-2 border rounded-lg p-3 bg-gray-50 max-h-48 overflow-y-auto">
                  <p className="text-xs text-gray-500 mb-2">
                    Select approved documents to attach:
                  </p>
                  <div className="space-y-2">
                    {approvedFiles.map((file) => (
                      <div 
                        key={file.id}
                        className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                          selectedFiles.includes(file.id) 
                            ? 'bg-blue-50 border-blue-300' 
                            : 'bg-white hover:border-blue-300'
                        }`}
                        onClick={() => handleFileToggle(file.id)}
                      >
                        <Checkbox
                          checked={selectedFiles.includes(file.id)}
                          onCheckedChange={() => handleFileToggle(file.id)}
                        />
                        <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="text-sm flex-1 truncate">{file.file_name}</span>
                        {file.file_size > 0 && (
                          <span className="text-xs text-gray-400 shrink-0">{formatFileSize(file.file_size)}</span>
                        )}
                        <span className="text-xs text-green-600 shrink-0">Approved</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Selected Files Preview */}
          {selectedFiles.length > 0 && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {selectedFiles.map(fileId => {
                  const file = availableFiles.find(f => f.id === fileId);
                  return file ? (
                    <div 
                      key={fileId}
                      className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-800 px-2.5 py-1.5 rounded-md text-sm"
                      data-testid={`selected-attachment-${fileId}`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                      <span className="truncate max-w-[180px]">{file.file_name}</span>
                      {file.file_size > 0 && (
                        <span className="text-xs text-blue-500 shrink-0">({formatFileSize(file.file_size)})</span>
                      )}
                      <button 
                        onClick={() => handleFileToggle(fileId)}
                        className="ml-0.5 hover:text-red-600 shrink-0"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : null;
                })}
              </div>
              {(() => {
                const totalSize = selectedFiles.reduce((sum, fid) => {
                  const f = availableFiles.find(x => x.id === fid);
                  return sum + (f?.file_size || 0);
                }, 0);
                return totalSize > 0 ? (
                  <p className="text-xs text-gray-500" data-testid="compose-total-attachment-size">
                    Total attachment size: <span className="font-medium text-gray-700">{formatFileSize(totalSize)}</span>
                    {totalSize > 20 * 1024 * 1024 && (
                      <span className="text-amber-600 ml-2">Large attachment — delivery may be affected</span>
                    )}
                  </p>
                ) : null;
              })()}
            </div>
          )}

          {/* Send Button */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || !toEmails.trim() || !subject.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="compose-send-btn"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Email
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
